import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Fingerprint, CheckCircle2, XCircle, ShieldCheck, ChevronLeft, Download, Save, Trash, Edit, X } from "lucide-react";
import dayjs from "dayjs";

import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Reactable } from "../../components/ui/reactable";
import { Button } from "../../components/ui/button";
import { cn } from "../../utils/tailwindUtils";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { checkUserPermissionFunc } from "../../components/common/CommonFunctions";
import { apiUrlFor, BIOMETRIC_DEVICE_CONFIG, BIOMETRIC_EMPLOYEE_MAPPING, BIOMETRIC_MAPPED_EMPLOYEES } from "../../constants/api";
import MappingUploadPanel from "../../components/biometric/MappingUploadPanel";
import { ConfirmPopupModal } from "../../components/ui/dialogModal";

/**
 * Biometric devices.
 *
 * Devices are shown, never edited here: Most of what this replaces is the
 * question "is that reader syncing?", which needs no write at all; and a wrong
 * direction or timezone silently corrupts attendance, which feeds payroll, so
 * editing is a later, more careful step.
 *
 * Credentials are never requested: the API reports that a connection exists
 * and under which account, and nothing here needs the password to say so.
 */

const DIRECTION_STYLES = {
    IN: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
    OUT: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
};

const Pill = ({ children, className }) => (
    <span className={cn("text-xs px-2 py-0.5 rounded-full", className)}>{children}</span>
);

const deviceColumns = () => [
    {
        accessor: "device_name", Header: "Device", sortable: true, filterable: true,
        Cell: ({ row }) => (
            <div>
                <div className="font-medium">{row.device_name || "—"}</div>
                <div className="text-xs text-muted-foreground font-mono">{row.device_serial_no}</div>
            </div>
        ),
    },
    {
        accessor: "location_name", Header: "Location", sortable: true,
        filterable: true, filterType: "select",
    },
    {
        accessor: "direction", Header: "Direction", sortable: true,
        filterable: true, filterType: "select",
        Cell: ({ row }) => (
            <Pill className={DIRECTION_STYLES[row.direction] ||
                "bg-gray-100 text-gray-700 dark:bg-gray-700/40 dark:text-gray-200"}>
                {row.direction || "—"}
            </Pill>
        ),
    },
    { accessor: "timezone", Header: "Timezone", sortable: true },
    {
        accessor: "is_attendance_device", Header: "Counts for Attendance", sortable: true,
        Cell: ({ row }) => (row.is_attendance_device
            ? <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 text-xs">
                <CheckCircle2 className="w-3.5 h-3.5" /> Yes
            </span>
            : <span className="inline-flex items-center gap-1 text-muted-foreground text-xs">
                <XCircle className="w-3.5 h-3.5" /> No
            </span>),
    },
    {
        accessor: "last_sync_on", Header: "Last Sync", sortable: true,
        // The column the screen exists for: a reader that stopped reporting
        // looks identical to one nobody punched on, until you can see when it
        // last spoke.
        Cell: ({ row }) => (row.last_sync_on
            ? <span>{dayjs(row.last_sync_on).format("DD-MM-YYYY HH:mm")}</span>
            : <span className="text-muted-foreground text-xs">Never synced</span>),
    },
];

const MappingEditContext = createContext(null);

const MappingFieldInput = ({ row, field, className }) => {
    const { drafts, editingId, setDraft } = useContext(MappingEditContext);
    const editing = editingId === row.id;
    const value = drafts[row.id]?.[field] ?? row[field] ?? "";
    return (
        <input
            type="text"
            value={value}
            onChange={(e) => setDraft(row.id, { [field]: e.target.value })}
            disabled={!editing}
            className={cn(
                "w-full rounded-md border px-2 py-1 font-mono text-sm",
                className,
                editing
                    ? "border-input bg-background"
                    : "border-transparent bg-transparent cursor-default",
            )}
        />
    );
};

const EmployeeCodeCell = ({ row }) => (
    <MappingFieldInput row={row} field="employee_id" className="max-w-[140px]" />
);

const BiometricCodeCell = ({ row }) => (
    <MappingFieldInput row={row} field="code" className="max-w-[120px]" />
);

const MappingActionsCell = ({ row }) => {
    const { editingId, savingId, onEdit, onCancel, onSave, onUnmap } = useContext(MappingEditContext);
    const editing = editingId === row.id;
    return (
        <div className="flex items-center gap-1">
            {editing ? (
                <>
                    <Button
                        variant="ghost"
                        size="sm"
                        disabled={savingId === row.id}
                        className="text-sidebar-primary hover:bg-blue-50 dark:hover:bg-gray-700"
                        onClick={() => onSave(row)}
                        title="Save"
                    >
                        <Save className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        disabled={savingId === row.id}
                        className="text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700"
                        onClick={() => onCancel(row.id)}
                        title="Cancel"
                    >
                        <X className="h-4 w-4" />
                    </Button>
                </>
            ) : (
                <Button
                    variant="ghost"
                    size="sm"
                    disabled={savingId === row.id}
                    className="text-sidebar-primary hover:bg-blue-50 dark:hover:bg-gray-700"
                    onClick={() => onEdit(row)}
                    title="Edit"
                >
                    <Edit className="h-4 w-4" />
                </Button>
            )}
            <Button
                variant="ghost"
                size="sm"
                disabled={savingId === row.id || editing}
                className="text-red-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30"
                onClick={() => onUnmap(row)}
                title="Unmap"
            >
                <Trash className="h-4 w-4" />
            </Button>
        </div>
    );
};

const MAPPING_COLUMNS = [
    {
        accessor: "employee_id", Header: "Employee Code", sortable: true, filterable: true,
        Cell: EmployeeCodeCell,
    },
    { accessor: "employee_name", Header: "Employee", sortable: true, filterable: true },
    {
        accessor: "code", Header: "Biometric Code", sortable: true, filterable: true,
        Cell: BiometricCodeCell,
    },
    { accessor: "name", Header: "Name on Device", sortable: true },
    {
        accessor: "action", Header: "Actions", sortable: false,
        Cell: MappingActionsCell,
    },
];

const BiometricDevices = () => {
    const { fetch, update, remove } = useApiMethods();
    const navigate = useNavigate();
    const goBack = () => navigate("/settings/integrations");
    // This screen lives under /settings, and "/settings" is an open prefix
    // matched before the route rules -- so PermissionGate lets anyone through
    // and the screen has to check for itself.
    const allowed = !!checkUserPermissionFunc("biometric", "biometric_devices", "SubMenu")?.permission;
    const [config, setConfig] = useState(null);
    const [mapped, setMapped] = useState([]);
    const [drafts, setDrafts] = useState({});
    const [editingId, setEditingId] = useState(null);
    const [savingId, setSavingId] = useState(null);
    const [unmapRow, setUnmapRow] = useState(null);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);

    const loadData = useCallback(async () => {
        if (!allowed) { setLoading(false); return; }
        setLoading(true);
        try {
            const [cfg, map] = await Promise.all([
                fetch(BIOMETRIC_DEVICE_CONFIG),
                fetch(BIOMETRIC_MAPPED_EMPLOYEES),
            ]);
            setConfig(cfg?.data || null);
            setMapped(map?.data || []);
            setDrafts({});
            setEditingId(null);
        } catch (_) {
            ToastMsg("error", "Failed to load biometric configuration");
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allowed]);

    useEffect(() => { loadData(); }, [loadData]);

    // The API nests devices under their location; the table wants one row per
    // device, carrying the location it belongs to.
    const devices = useMemo(() => {
        const out = [];
        for (const loc of config?.locations || []) {
            for (const dev of loc.devices || []) {
                out.push({ ...dev, location_name: loc.location_name, location_id: loc.location_id });
            }
        }
        return out;
    }, [config]);

    const connection = config?.connection;
    // The download and the file upload both talk to one reader location.
    // That location is the first device row.
    const locationId = devices[0]?.location_id;

    const setDraft = useCallback((id, patch) => {
        setDrafts((prev) => ({
            ...prev,
            [id]: { ...(prev[id] || {}), ...patch },
        }));
    }, []);

    const startEdit = useCallback((row) => {
        setEditingId(row.id);
        setDrafts((prev) => ({
            ...prev,
            [row.id]: {
                employee_id: row.employee_id || "",
                code: row.code || "",
            },
        }));
    }, []);

    const cancelEdit = useCallback((id) => {
        setEditingId(null);
        setDrafts((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
        });
    }, []);

    const saveMapping = useCallback(async (row) => {
        const draft = drafts[row.id] || {};
        const employee_id = (draft.employee_id ?? row.employee_id ?? "").trim();
        const code = (draft.code ?? row.code ?? "").trim();
        if (!employee_id || !code) {
            ToastMsg("error", "Employee code and biometric code are required");
            return;
        }
        setSavingId(row.id);
        try {
            const res = await update(BIOMETRIC_MAPPED_EMPLOYEES, {
                id: row.id,
                employee_id,
                code,
            });
            if (res?.status === "success") {
                ToastMsg("success", res.message || "Mapping updated");
                await loadData();
            } else {
                ToastMsg("error", res?.message || "Could not update mapping");
            }
        } catch (_) {
            ToastMsg("error", "Could not update mapping");
        } finally {
            setSavingId(null);
        }
    }, [drafts, update, loadData]);

    const mappingEdit = useMemo(() => ({
        drafts,
        editingId,
        savingId,
        setDraft,
        onEdit: startEdit,
        onCancel: cancelEdit,
        onSave: saveMapping,
        onUnmap: setUnmapRow,
    }), [drafts, editingId, savingId, setDraft, startEdit, cancelEdit, saveMapping]);

    const confirmUnmap = async () => {
        if (!unmapRow) return;
        setSavingId(unmapRow.id);
        try {
            const res = await remove(`${BIOMETRIC_MAPPED_EMPLOYEES}?id=${unmapRow.id}`, { id: unmapRow.id });
            if (res?.status === "success") {
                ToastMsg("success", res.message || "Mapping removed");
                setUnmapRow(null);
                await loadData();
            } else {
                ToastMsg("error", res?.message || "Could not remove mapping");
            }
        } catch (_) {
            ToastMsg("error", "Could not remove mapping");
        } finally {
            setSavingId(null);
        }
    };

    const downloadCsv = async () => {
        if (locationId === null || locationId === undefined || locationId === "") {
            ToastMsg("error", "No device location is available");
            return;
        }
        const companyId = JSON.parse(sessionStorage.getItem("customConfig") || "{}").company_id || "";
        const params = new URLSearchParams();
        params.set("locations", String(locationId));
        if (companyId !== "") params.set("company_id", String(companyId));
        const token = sessionStorage.getItem("token") || "";

        setDownloading(true);
        try {
            const response = await fetch(`${apiUrlFor(BIOMETRIC_EMPLOYEE_MAPPING)}?${params.toString()}`, {
                headers: token ? { Authorization: `token ${token}` } : {},
            });
            if (!response.ok) {
                let message = "Could not download the CSV";
                try {
                    const err = await response.json();
                    if (err?.message) message = err.message;
                } catch (_) { /* body was not JSON */ }
                ToastMsg("error", message);
                return;
            }
            const disposition = response.headers.get("content-disposition") || "";
            const named = disposition.match(/filename="([^"]+)"/i) || disposition.match(/filename=([^;]+)/i);
            const fileName = named
                ? named[1].trim().replace(/^["']|["']$/g, "")
                : "employees.csv";
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
        } catch (_) {
            ToastMsg("error", "Could not download the CSV");
        } finally {
            setDownloading(false);
        }
    };

    if (!allowed) {
        return (
            <ContainerCard scrollable>
                <button
                    type="button"
                    onClick={goBack}
                    className="mb-4 inline-flex items-center gap-1 text-sm font-medium text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
                >
                    <ChevronLeft className="h-4 w-4" />
                    Integrations
                </button>
                <div className="flex flex-col items-center justify-center py-20 text-center gap-3">
                    <ShieldCheck className="w-12 h-12 text-amber-500" />
                    <p className="text-sm text-muted-foreground max-w-sm">
                        You don't have permission to manage the biometric integration.
                    </p>
                </div>
            </ContainerCard>
        );
    }

    return (
        <ContainerCard scrollable>
            <div className="flex items-start gap-1">
                <button
                    type="button"
                    onClick={goBack}
                    aria-label="Back to integrations"
                    className="mt-3.5 p-1.5 rounded-md text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800"
                >
                    <ChevronLeft className="h-5 w-5" />
                </button>
                <div className="flex-1 min-w-0">
                    <ModuleTitleCard
                        Icon={Fingerprint}
                        title="Biometric Devices"
                        subtitle="Readers reporting into attendance, and the employees mapped to them"
                        totalRecords={devices.length}
                    />
                </div>
            </div>

            {loading ? (
                <div className="text-center py-10 text-muted-foreground">Loading…</div>
            ) : (
                <div className="space-y-4">
                    {/* <div className="bg-white dark:bg-gray-800 rounded-lg border border-border p-4">
                        <h3 className="text-sm font-semibold mb-2">Connection</h3>
                        {connection?.configured ? (
                            <div className="text-sm space-y-0.5">
                                <div>
                                    <span className="text-muted-foreground">Server: </span>
                                    <span className="font-mono text-xs">{connection.api_url}</span>
                                </div>
                                <div>
                                    <span className="text-muted-foreground">API user: </span>
                                    {connection.api_username}
                                </div>
                                <div>
                                    <span className="text-muted-foreground">System user: </span>
                                    {connection.system_user}
                                </div>
                                <p className="text-xs text-muted-foreground pt-1">
                                    Passwords are never sent to this screen. Contact support to change
                                    the connection.
                                </p>
                            </div>
                        ) : (
                            <p className="text-sm text-muted-foreground">
                                No biometric connection is configured for your organisation yet.
                            </p>
                        )}
                    </div> */}

                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-border p-4">
                        <div className="flex items-center justify-between gap-3 mb-3">
                            <h3 className="text-sm font-semibold">
                                Devices{devices.length ? ` (${devices.length})` : ""}
                            </h3>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={downloading}
                                onClick={downloadCsv}
                            >
                                <Download className="w-4 h-4" />
                                {downloading ? "Downloading…" : "Download CSV"}
                            </Button>
                        </div>
                        {devices.length === 0 ? (
                            <p className="text-sm text-muted-foreground py-6 text-center">
                                No devices are registered yet.
                            </p>
                        ) : (
                            <Reactable
                                pagename="biometric-devices"
                                data={devices}
                                setData={() => { }}
                                columns={deviceColumns()}
                                rowSize="sm"
                            />
                        )}
                    </div>

                    <MappingUploadPanel
                        locationId={locationId}
                        onApplied={loadData}
                    />

                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-border p-4">
                        <h3 className="text-sm font-semibold mb-1">
                            Mapped Employees{mapped.length ? ` (${mapped.length})` : ""}
                        </h3>
                        <p className="text-xs text-muted-foreground mb-3">
                            Click Edit to change the employee code or biometric code, then Save. Name on device comes from the reader.
                        </p>
                        {mapped.length === 0 ? (
                            <p className="text-sm text-muted-foreground py-6 text-center">
                                No employees are mapped to a biometric code yet.
                            </p>
                        ) : (
                            <MappingEditContext.Provider value={mappingEdit}>
                                <Reactable
                                    pagename="biometric-mapped-employees"
                                    data={mapped}
                                    setData={setMapped}
                                    columns={MAPPING_COLUMNS}
                                    rowSize="sm"
                                />
                            </MappingEditContext.Provider>
                        )}
                    </div>
                </div>
            )}

            <ConfirmPopupModal
                open={!!unmapRow}
                onClose={() => setUnmapRow(null)}
                onConfirm={confirmUnmap}
                title="Remove mapping"
                description={
                    unmapRow
                        ? `Unmap ${unmapRow.employee_id || "this employee"} from biometric code ${unmapRow.code || ""}? Attendance will stop matching this reader code until it is mapped again.`
                        : ""
                }
                type="warning"
                confirmLabel="Unmap"
                destructive
                loading={savingId != null && unmapRow && savingId === unmapRow.id}
            />
        </ContainerCard>
    );
};

export default BiometricDevices;
